/* eslint-disable react-refresh/only-export-components */

import ExclamationMarkIcon from "@components/common/icons/exclamation-mark.icon";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, ButtonGroup, Typography } from "@mui/material";
import type { ColumnDef } from "@tanstack/react-table";
import type { Delivery, DeliveryType } from "@types_def/models/delivery.types";
import { getDeliveryTypeColor } from "@utils/functions/Delivery/DeliveryType/deliveryTypeAttributes";
import { enumToStr } from "@utils/strEnum";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { formatDeliveryTableDate } from "@/utils/date";
import { formatEuro } from "@/utils/delivery-pricing";

const deliveryStatusLabels: Record<Delivery["status"], string> = {
  REQUESTED: "Nouvelle",
  PENDING_CUSTOMER_CONFIRMATION: "En attente de confirmation",
  CONFIRMED_BY_CUSTOMER: "Confirmée par le client",
  RESCHEDULED_BY_ADMIN: "Replanifiée",
  CONFIRMED: "Confirmé",
  WAITING_FOR_DRIVER: "En attente du chauffeur",
  ALERT: "Alerte",
  SMS_SENT: "SMS envoyé",
  CANCELLED: "Annulée",
  CANCELLED_BY_ADMIN: "Annulée par l'administrateur",
  CANCELLED_BY_CUSTOMER: "Annulée par le client",
  CANCELLED_BY_MANDATORY: "Annulée par le mandataire",
  SET: "Attributer",
  PICKUP_DRIVER: "Pris en charge",
  PICKUP_CLIENT: "Validation de livraison",
  ONGOING: "En route",
  DELIVERED: "Voiture Livré",
  SWAP_PICKUP_DRIVER: "Voiture 2, Pris en charge",
  SWAP_IN_TRANSIT: "Voiture 2, En route",
  SWAP_PICKUP_CLIENT: "Voiture 2, Validation de livraison",
  SWAP_DELIVERED: "Voiture 2, Livré",
  COMPLETED: "Complété",
};

function MarginPriceCell({ children }: { children: Delivery }) {
  const { finalPrice, clientFinalPrice } = children;
  if (finalPrice == null || clientFinalPrice == null) return <i>Error</i>;

  return <div>{(clientFinalPrice - finalPrice).toFixed(2)} € HT</div>;
}

function AdminDeliveredDeliveryDetailsActionCell({ children }: { children: Delivery }) {
  const { t } = useTranslation();
  const { type, status, id } = children;
  const strType = enumToStr<DeliveryType>(type);
  const strStatus = enumToStr<typeof status>(status);

  return (
    <ButtonGroup
      disableElevation
      variant="contained"
      size="small"
      sx={{ gap: 1, width: "200px" }}
      orientation="vertical"
    >
      <RouterLink to={`/admin/pilot/details/${strType}/${strStatus}/${id}`}>
        <Button color="primary" variant="contained" size="small" fullWidth>
          <VisibilityIcon fontSize="small" />
          {t("delivery.action.details")}
        </Button>
      </RouterLink>
    </ButtonGroup>
  );
}

function ContractCell({ children }: { children: Delivery }) {
  const { type, publicId, status } = children;
  const cancelled = [
    "CANCELLED_BY_CUSTOMER",
    "CANCELLED_BY_ADMIN",
    "CANCELLED_BY_MANDATORY",
  ].includes(status);
  const color = getDeliveryTypeColor(type);

  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      {cancelled && (
        <Box
          sx={{
            width: "fit-content",
            height: "16px",
            "& svg": {
              mr: 0,
              "& *": {
                fill: "#d81a1a",
              },
            },
          }}
        >
          <ExclamationMarkIcon />
        </Box>
      )}
      <div
        style={{
          borderRadius: "50%",
          width: "20px",
          height: "20px",
          background: color,
        }}
      />
      <div>{publicId}</div>
    </div>
  );
}

export const DRIVER_HISTORY_COLUMNS: ColumnDef<Delivery>[] = [
  {
    id: "publicId",
    accessorKey: "publicId",
    enableSorting: true,
    meta: { label: "Contrat" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Contrat" />,
    cell: ({ row }) => <ContractCell>{row.original}</ContractCell>,
  },
  {
    id: "fromAddress",
    accessorFn: (row) => row.fromAddress?.city ?? "",
    enableSorting: false,
    meta: { label: "Arrivée" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Arrivée" />,
    cell: ({ row }) => {
      const delivery = row.original;
      const departedAt = delivery.departedAt;

      return (
        <b>
          {delivery.fromAddress?.city}
          {departedAt && <>-{formatDeliveryTableDate(departedAt)}</>}
        </b>
      );
    },
  },
  {
    id: "toAddress",
    accessorFn: (row) => row.toAddress?.city ?? "",
    enableSorting: false,
    meta: { label: "Départ" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Départ" />,
    cell: ({ row }) => {
      const delivery = row.original;
      const completedAt = delivery.completedAt;

      return (
        <b>
          {delivery.toAddress?.city}
          {completedAt && <>-{formatDeliveryTableDate(completedAt)}</>}
        </b>
      );
    },
  },
  {
    id: "finalPrice",
    accessorKey: "finalPrice",
    enableSorting: true,
    meta: { label: "Prix Final Driver" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Prix Final Driver" />,
    cell: ({ row }) => (
      <div>
        {row.original.finalPrice != null ? `${formatEuro(row.original.finalPrice)}` : "P/A"}
      </div>
    ),
  },
  {
    id: "margin",
    accessorFn: (row) => row.clientFinalPrice ?? 0,
    enableSorting: false,
    meta: { label: "Margin" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Margin" />,
    cell: ({ row }) => <MarginPriceCell>{row.original}</MarginPriceCell>,
  },
  {
    id: "clientFinalPrice",
    accessorKey: "clientFinalPrice",
    enableSorting: true,
    meta: { label: "Montant HT Client" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Montant HT Client" />,
    cell: ({ row }) => (
      <div>
        {row.original.clientFinalPrice != null
          ? `${row.original.clientFinalPrice.toString().replace(".", ",")} € HT`
          : "P/A"}
      </div>
    ),
  },
  {
    id: "organizationId",
    accessorFn: (row) => row.organization?.name ?? "",
    enableSorting: true,
    meta: { label: "Organisation" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Organisation" />,
    cell: ({ row }) => <div>{row.original.organization?.name ?? "Non attribué"}</div>,
  },
  {
    id: "status",
    accessorKey: "status",
    enableSorting: true,
    meta: { label: "Status" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => <>{deliveryStatusLabels[row.original.status] ?? "Unknown"}</>,
  },
  {
    id: "action",
    accessorKey: "id",
    enableSorting: false,
    meta: { label: "Action", align: "center" },
    header: () => <Typography variant="subtitle1">Action</Typography>,
    cell: ({ row }) => (
      <AdminDeliveredDeliveryDetailsActionCell>
        {row.original}
      </AdminDeliveredDeliveryDetailsActionCell>
    ),
  },
];
